import { z } from 'zod';

/**
 * Shared bounds + skip rules for VCS compare and Execution Agent workspace scans.
 * Single source of truth — agent and API must both import from here (audit S-1).
 */

export const SOURCE_READ_MAX_FILES = 500;
export const SOURCE_READ_MAX_FILE_BYTES = 200_000;
export const SOURCE_READ_MAX_TOTAL_BYTES = 2_000_000;
export const SOURCE_READ_MAX_DEPTH = 12;
export const SOURCE_READ_MAX_SCAN_MS = 120_000;

export const SOURCE_READ_ACCESS_MODES = ['git_compare', 'workspace_scan'] as const;
export type SourceReadAccessMode = (typeof SOURCE_READ_ACCESS_MODES)[number];

export const SOURCE_FILE_KINDS = [
  'typescript',
  'javascript',
  'tsx',
  'jsx',
  'java',
  'python',
  'go',
  'csharp',
  'kotlin',
  'json',
  'yaml',
  'config',
  'other-text',
  'unsupported',
] as const;
export type SourceFileKind = (typeof SOURCE_FILE_KINDS)[number];

const SECRET_NAMES = new Set([
  '.env',
  '.env.local',
  '.env.production',
  '.env.development',
  'id_rsa',
  'id_ed25519',
  'id_ecdsa',
  'credentials.json',
  'service-account.json',
]);

const SECRET_EXT = new Set(['pem', 'key', 'p12', 'pfx', 'p8', 'keystore', 'jks']);

const LOCKFILE_NAMES = new Set([
  'package-lock.json',
  'yarn.lock',
  'pnpm-lock.yaml',
  'Cargo.lock',
  'poetry.lock',
  'composer.lock',
  'go.sum',
  'Gemfile.lock',
  'Pipfile.lock',
]);

const BINARY_EXT = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'ico',
  'pdf',
  'zip',
  'gz',
  'tgz',
  'woff',
  'woff2',
  'ttf',
  'eot',
  'mp4',
  'webm',
  'wasm',
  'exe',
  'dll',
  'so',
  'dylib',
  'bin',
  'o',
  'class',
  'jar',
  'war',
  'ear',
  'sqlite',
  'db',
]);

/** Directories always pruned during workspace scan (never entered). */
export const SOURCE_READ_ALWAYS_PRUNE_DIRS = new Set([
  'node_modules',
  '.git',
  'dist',
  'build',
  'coverage',
  'vendor',
  '.next',
  'out',
  'target',
  '__pycache__',
  '.turbo',
  '.cache',
  '.pnpm-store',
  '.gradle',
  '.idea',
  '.vscode',
  'tmp',
  'temp',
]);

const EXT_TO_KIND: Record<string, SourceFileKind> = {
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  mjs: 'javascript',
  cjs: 'javascript',
  java: 'java',
  py: 'python',
  go: 'go',
  cs: 'csharp',
  kt: 'kotlin',
  kts: 'kotlin',
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
};

const CONFIG_NAMES = new Set([
  'package.json',
  'pnpm-workspace.yaml',
  'pnpm-workspace.yml',
  'lerna.json',
  'go.mod',
  'pyproject.toml',
  'requirements.txt',
  'pom.xml',
  'build.gradle',
  'settings.gradle',
  'Cargo.toml',
]);

export function sourceReadBasename(filePath: string): string {
  const trimmed = filePath.replace(/\\/g, '/').replace(/\/+$/, '');
  const slash = trimmed.lastIndexOf('/');
  return slash === -1 ? trimmed : trimmed.slice(slash + 1);
}

export function sourceReadExtOf(filePath: string): string {
  const base = sourceReadBasename(filePath).toLowerCase();
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return '';
  return base.slice(dot + 1);
}

/** True when the path must not be read into ephemeral cache or scan bodies. */
export function skipEphemeralRead(filePath: string): boolean {
  const base = sourceReadBasename(filePath).toLowerCase();
  if (SECRET_NAMES.has(base)) return true;
  if (base.startsWith('.env.')) return true;
  if (LOCKFILE_NAMES.has(base)) return true;
  const ext = sourceReadExtOf(filePath);
  if (SECRET_EXT.has(ext)) return true;
  if (BINARY_EXT.has(ext)) return true;
  return false;
}

export function classifySourceFileKind(relativePath: string): SourceFileKind {
  const base = sourceReadBasename(relativePath);
  if (CONFIG_NAMES.has(base)) return 'config';
  const ext = sourceReadExtOf(relativePath);
  if (ext && EXT_TO_KIND[ext]) return EXT_TO_KIND[ext];
  if (!ext && base === 'Dockerfile') return 'config';
  if (BINARY_EXT.has(ext)) return 'unsupported';
  if (ext) return 'other-text';
  return 'other-text';
}

export function isSniffableTextKind(kind: SourceFileKind): boolean {
  return kind !== 'unsupported';
}

export function normalizeRepoRelativePath(raw: string): string {
  return raw.replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\/+/, '').trim();
}

export function uniqueRepoPaths(paths: readonly string[], max = SOURCE_READ_MAX_FILES): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of paths) {
    const path = normalizeRepoRelativePath(raw);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    out.push(path);
    if (out.length >= max) break;
  }
  return out;
}

export const SourceFileEntrySchema = z.object({
  relativePath: z.string(),
  kind: z.enum(SOURCE_FILE_KINDS),
  sizeBytes: z.number().int().nonnegative(),
  packageRoot: z.string().optional(),
});
export type SourceFileEntry = z.infer<typeof SourceFileEntrySchema>;

export const WorkspacePackageBoundarySchema = z.object({
  relativePath: z.string(),
  marker: z.string(),
  kind: z.enum(['npm', 'pnpm-workspace', 'maven', 'gradle', 'go', 'python', 'dotnet', 'cargo', 'other']),
});
export type WorkspacePackageBoundary = z.infer<typeof WorkspacePackageBoundarySchema>;

export const GitWorkspaceMetadataSchema = z.object({
  detected: z.literal(true),
  repositoryRoot: z.string(),
  headSha: z.string().optional(),
  branch: z.string().optional(),
  dirty: z.boolean().optional(),
});
export type GitWorkspaceMetadata = z.infer<typeof GitWorkspaceMetadataSchema>;

export const WorkspaceScanMetadataSchema = z.object({
  partial: z.boolean(),
  partialReasons: z.array(z.string()).max(20),
  scannedFileCount: z.number().int().nonnegative(),
  workspaceRoot: z.string(),
  entries: z.array(SourceFileEntrySchema).max(SOURCE_READ_MAX_FILES),
  packages: z.array(WorkspacePackageBoundarySchema).max(200),
  git: GitWorkspaceMetadataSchema.optional(),
});
export type WorkspaceScanMetadata = z.infer<typeof WorkspaceScanMetadataSchema>;

export const SourceReadBoundsSchema = z.object({
  maxFiles: z.number().int().positive().max(SOURCE_READ_MAX_FILES).default(SOURCE_READ_MAX_FILES),
  maxFileBytes: z.number().int().positive().max(SOURCE_READ_MAX_FILE_BYTES).default(SOURCE_READ_MAX_FILE_BYTES),
  maxTotalBytes: z.number().int().positive().max(SOURCE_READ_MAX_TOTAL_BYTES).default(SOURCE_READ_MAX_TOTAL_BYTES),
  maxDepth: z.number().int().positive().max(SOURCE_READ_MAX_DEPTH).default(SOURCE_READ_MAX_DEPTH),
  maxScanMs: z.number().int().positive().max(SOURCE_READ_MAX_SCAN_MS).default(SOURCE_READ_MAX_SCAN_MS),
});
export type SourceReadBounds = z.infer<typeof SourceReadBoundsSchema>;

export const DEFAULT_SOURCE_READ_BOUNDS: SourceReadBounds = SourceReadBoundsSchema.parse({});
